import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaPlus, FaGlobe, FaCheck, FaClock, FaTimes,
  FaSpinner, FaNewspaper, FaRss, FaExternalLinkAlt, FaTrash
} from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';

const CATEGORIES = [
  { id: 'france', label: 'France', icon: '🇫🇷' },
  { id: 'afrique', label: 'Afrique', icon: '🌍' },
  { id: 'international', label: 'International', icon: '🌐' },
  { id: 'sport', label: 'Sport', icon: '⚽' },
  { id: 'economie', label: 'Économie', icon: '📈' },
  { id: 'culture', label: 'Culture', icon: '🎭' },
  { id: 'tech', label: 'Tech', icon: '💻' },
  { id: 'autre', label: 'Autre', icon: '📰' }
];

const SOURCE_TYPES = [
  'Presse nationale',
  'Presse locale',
  'Presse régionale',
  'Agence de presse',
  'Radio',
  'Télévision',
  'Pure player',
  'Presse économique',
  'Presse sportive',
  'Magazine',
  'Autre'
];

const QuizSourcesPage = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState({});
  const [customSources, setCustomSources] = useState([]);
  const [pendingSources, setPendingSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    category: 'france',
    sourceType: 'Presse nationale',
    description: '',
    rssUrl: ''
  });

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      // Sources globales
      const allResponse = await api.get('/api/settings/sources');
      setSources(allResponse.data.sources || {});
      
      // Sources personnalisées de l'école
      const schoolResponse = await api.get('/api/settings/sources/school');
      setCustomSources(schoolResponse.data.sources || []);
      setPendingSources(schoolResponse.data.pending || []);
      
    } catch (error) {
      console.error('Erreur chargement sources:', error);
      toast.error('Erreur lors du chargement des sources');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.url.trim()) {
      toast.error('Veuillez remplir le nom et l\'URL');
      return;
    }
    
    // Valider l'URL
    try {
      new URL(formData.url);
    } catch {
      toast.error('URL invalide');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await api.post('/api/settings/sources/request', formData);
      
      toast.success('Demande envoyée ! Elle sera examinée par l\'équipe.');
      setShowForm(false);
      setFormData({
        name: '',
        url: '',
        category: 'france',
        sourceType: 'Presse nationale',
        description: '',
        rssUrl: ''
      });
      fetchSources();
      
    } catch (error) {
      console.error('Erreur soumission:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const getFilteredSources = () => {
    if (selectedCategory === 'all') {
      return Object.entries(sources).flatMap(([cat, srcs]) => 
        srcs.map(s => ({ ...s, category: cat }))
      );
    }
    return (sources[selectedCategory] || []).map(s => ({ ...s, category: selectedCategory }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#1B4965]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B4965] to-[#2D5A7B] text-white py-6 px-4">
        <div className="container mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <FaArrowLeft /> Retour
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Sources d'information</h1>
              <p className="text-white/70 mt-1">Gérez les sources utilisées pour les quiz d'actualité</p>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#E63946] hover:bg-[#C1121F]"
              data-testid="add-source-btn"
            >
              <FaPlus className="mr-2" /> Proposer une source
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-[#1B4965]">
                {Object.values(sources).flat().length}
              </div>
              <div className="text-sm text-gray-500">Sources actives</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">
                {customSources.length}
              </div>
              <div className="text-sm text-gray-500">Personnalisées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {pendingSources.length}
              </div>
              <div className="text-sm text-gray-500">En attente</div>
            </CardContent>
          </Card>
        </div>

        {/* Demandes en attente */}
        {pendingSources.length > 0 && (
          <Card className="mb-8 border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
                <FaClock /> Demandes en attente d'approbation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingSources.map((source, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FaNewspaper className="text-yellow-600" />
                      <div>
                        <span className="font-medium">{source.name}</span>
                        <span className="text-sm text-gray-500 ml-2">({source.category})</span>
                      </div>
                    </div>
                    <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                      En attente
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtres par catégorie */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === 'all' 
                ? 'bg-[#1B4965] text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Toutes
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat.id 
                  ? 'bg-[#1B4965] text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Liste des sources */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getFilteredSources().map((source, idx) => (
            <motion.div
              key={`${source.category}-${idx}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className={`hover:shadow-md transition-shadow ${source.custom ? 'border-green-200' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        source.custom ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        <FaNewspaper className={source.custom ? 'text-green-600' : 'text-blue-600'} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{source.name}</h3>
                        <p className="text-xs text-gray-500">{source.type}</p>
                      </div>
                    </div>
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#1B4965]"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {CATEGORIES.find(c => c.id === source.category)?.icon} {source.category}
                    </span>
                    {source.custom && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <FaCheck /> Personnalisée
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Modal Formulaire */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowForm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-[#1B4965] mb-6">
                    Proposer une nouvelle source
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Nom */}
                    <div>
                      <Label htmlFor="name">Nom de la source *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Ex: France 24, Mediapart..."
                        required
                        data-testid="source-name-input"
                      />
                    </div>
                    
                    {/* URL */}
                    <div>
                      <Label htmlFor="url">URL du site *</Label>
                      <Input
                        id="url"
                        name="url"
                        type="url"
                        value={formData.url}
                        onChange={handleChange}
                        placeholder="https://www.example.com"
                        required
                        data-testid="source-url-input"
                      />
                    </div>
                    
                    {/* Catégorie & Type */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="category">Catégorie</Label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          className="w-full border rounded-lg px-3 py-2"
                          data-testid="source-category-select"
                        >
                          {CATEGORIES.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.icon} {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="sourceType">Type de média</Label>
                        <select
                          id="sourceType"
                          name="sourceType"
                          value={formData.sourceType}
                          onChange={handleChange}
                          className="w-full border rounded-lg px-3 py-2"
                          data-testid="source-type-select"
                        >
                          {SOURCE_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label htmlFor="description">Description (optionnel)</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Décrivez brièvement cette source..."
                        rows={2}
                        data-testid="source-description-input"
                      />
                    </div>
                    
                    {/* RSS */}
                    <div>
                      <Label htmlFor="rssUrl">
                        <FaRss className="inline mr-1" /> Flux RSS (optionnel)
                      </Label>
                      <Input
                        id="rssUrl"
                        name="rssUrl"
                        type="url"
                        value={formData.rssUrl}
                        onChange={handleChange}
                        placeholder="https://www.example.com/rss.xml"
                        data-testid="source-rss-input"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
                      <p>
                        ℹ️ Votre demande sera examinée par l'équipe DailyGlobe. 
                        Une fois approuvée, cette source sera utilisée pour générer 
                        les quiz d'actualité de votre école.
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowForm(false)}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-[#1B4965] hover:bg-[#15384F]"
                        data-testid="submit-source-btn"
                      >
                        {submitting ? (
                          <FaSpinner className="animate-spin mr-2" />
                        ) : (
                          <FaPlus className="mr-2" />
                        )}
                        Proposer
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuizSourcesPage;
